import _ from 'lodash';
import { processChunks } from '@rogoag/shared-ts';
import { IDBPDatabase, IDBPObjectStore, openDB } from 'idb';
import getVersion from './version';
import download from 'downloadjs';
import * as Sentry from '@sentry/react';
import { MS_PER_MIN } from './constants';
import { getCurrentMission } from './dataModelHelpers';
import { PartialBy } from './types/types';
import { mcApi, McApiLogEntry } from './services/McApi';
import { serializeLogData } from './utils/serializeLogData';

const MISSION_CONTROL_EVENT_LOG_DB_NAME = 'missioncontrol-event-log';
const MISSION_CONTROL_EVENT_LOG_STORE_NAME = 'EventLog';
const MISSION_CONTROL_EVENT_LOG_UPLOAD_INTERVAL = 60 * 1000;
const MISSION_CONTROL_EVENT_LOG_UPLOAD_MAX_LOGS = 1000;
const MISSION_CONTROL_EVENT_LOG_EXPIRATION_PERIOD_DAYS = 2;
const MISSION_CONTROL_EVENT_LOG_INDIVIDUAL_UPLOAD_TIMEOUT = 3 * 1000;
const MISSION_CONTROL_EVENT_LOG_OVERALL_UPLOAD_TIMEOUT = 10 * 1000;
const MISSION_CONTROL_EVENT_LOG_UPLOAD_MAX_CONCURRENT = 6;

interface LogEntry {
  timestamp: string;
  userID: string;
  robot: string;
  lastConnectedRobot: string;
  logSessionID: string | null;
  eventType: string;
  appVersion: string;
  status: string;
  data: string | null;
  jobId: string | undefined;
  durationMS: number;
}

export class MissionControlEventLogger {
  db: IDBPDatabase<unknown> | null;
  uploadInterval: NodeJS.Timeout;
  uploadIntervalStarted: boolean;
  userID: string;
  robot: string;
  lastConnectedRobot: string;
  logSessionID: string | null;
  initialized: boolean;
  disabled: boolean;
  recentLogs: Record<string, [LogEntry, number]> = {};
  private static initializationPromise: Promise<void> | null = null; // Static lock
  private instanceId = Math.random().toString(36).slice(2); // For debugging

  constructor() {
    this.db = null;
    this.uploadIntervalStarted = false;
    this.userID = localStorage.getItem('username') || 'no-user';
    this.robot = localStorage.getItem('robot') || 'no-robot';
    this.lastConnectedRobot = localStorage.getItem('lastConnectedRobot') || 'no-robot';
    this.logSessionID = localStorage.getItem('logSessionID');

    if (!this.logSessionID) {
      this.logSessionID = Math.floor(Math.random() * 100000).toString();
      localStorage.setItem('logSessionID', this.logSessionID);
    }

    this.initialized = false;
    const isAdvancedModeDisabled: boolean = JSON.parse(localStorage.getItem('eventLogDisabled') ?? 'false');
    const isEnvironmentVariableDisabled = import.meta.env.VITE_EVENT_LOGGING !== 'on';
    this.disabled = isAdvancedModeDisabled || isEnvironmentVariableDisabled;
    console.debug('Logger', { isAdvancedModeDisabled, isEnvironmentVariableDisabled });
  }

  // TODO this seems to possibly not be set on recovery loads
  setUser(userID: string = '') {
    if (userID) {
      localStorage.setItem('username', userID);
      this.userID = userID;
    } else {
      localStorage.removeItem('username');
      this.userID = 'no-user';
    }
  }

  setRobot(robot: string = '') {
    if (robot) {
      localStorage.setItem('robot', robot);
      localStorage.setItem('lastConnectedRobot', robot);
      Sentry.setTag('robot', robot);
      this.robot = robot;
      this.lastConnectedRobot = robot;
    } else {
      localStorage.removeItem('robot');
      this.robot = 'no-robot';
    }
  }

  async toggleEventLogUploading() {
    console.debug(`Logger[${this.instanceId}] toggleEventLogUploading`);
    this.disabled = !this.disabled;

    if (this.disabled) {
      this._cancelUploadCycle();
    } else {
      await this._startUploadCycle(true);
    }

    localStorage.setItem('eventLogDisabled', JSON.stringify(this.disabled));
  }

  async logPeriodic(eventType: string, data?, periodMS = MS_PER_MIN, timestampOVRD?: any) {
    if (this.disabled) {
      return;
    }

    await this.initialize();

    if (!this.initialized) {
      console.warn(`Logger[${this.instanceId}]: Not initialized, skipping logPeriodic`);

      return;
    }

    const logKey = `${eventType}-${periodMS}`;

    const appVersion = getVersion();
    const timestamp = timestampOVRD ? new Date(timestampOVRD).toISOString() : new Date().toISOString();

    const logEntry: LogEntry = {
      timestamp,
      userID: this.userID,
      robot: this.robot,
      lastConnectedRobot: this.lastConnectedRobot,
      logSessionID: this.logSessionID,
      eventType: `${eventType} (periodic on ${periodMS}ms)`,
      appVersion,
      status: 'local',
      data: null,
      durationMS: 0,
      jobId: getCurrentMission()?.job_id,
    };

    if (data) {
      logEntry.data = serializeLogData(data);
    }

    if (this.recentLogs[logKey]) {
      const [recentLog, count] = this.recentLogs[logKey];
      const now = new Date().getTime();
      const recentLogTime = new Date(recentLog.timestamp).getTime();
      const timeSinceLastLog = now - recentLogTime;

      // if logged in last periodMS, do not log
      if (timeSinceLastLog < periodMS) {
        this.recentLogs[logKey] = [recentLog, count + 1];

        return;
      }

      recentLog.eventType = `${eventType} (periodic on ${periodMS}ms)` + (count > 0 ? ` (${count} times)` : '');
    }

    // remove old log
    this.recentLogs[logKey] = [logEntry, 0];

    if (this.db) {
      await this.db.add(MISSION_CONTROL_EVENT_LOG_STORE_NAME, logEntry);
    }
  }

  async log(eventType: string, data?, durationMS = 0, timestampOVRD?: any) {
    if (this.disabled) {
      return;
    }

    await this.initialize();

    if (!this.initialized) {
      console.warn(`Logger[${this.instanceId}]: Not initialized, skipping log`);

      return;
    }

    const appVersion = getVersion();

    const timestamp = timestampOVRD ? new Date(timestampOVRD).toISOString() : new Date().toISOString();

    const logEntry: LogEntry = {
      timestamp,
      userID: this.userID,
      robot: this.robot,
      lastConnectedRobot: this.lastConnectedRobot,
      logSessionID: this.logSessionID,
      eventType,
      appVersion,
      status: 'local',
      data: null,
      durationMS: 0,
      jobId: getCurrentMission()?.job_id,
    };

    if (data) {
      logEntry.data = serializeLogData(data);
    }

    if (durationMS > 0) {
      logEntry.durationMS = durationMS;
    }

    console.debug(`Logger[${this.instanceId}] log`, logEntry);

    if (this.db) {
      await this.db.add(MISSION_CONTROL_EVENT_LOG_STORE_NAME, logEntry);
    }
  }

  start(eventType: string, startingData) {
    return new LogTimer(eventType, startingData);
  }

  async stop(logTimer: LogTimer, endingData?) {
    if (this.disabled) {
      console.debug(`Logger[${this.instanceId}]: Logging disabled, skipping stop`);

      return;
    }

    await this.initialize();

    if (!this.initialized) {
      console.warn(`Logger[${this.instanceId}]: Not initialized, skipping stop`);

      return;
    }

    if (!logTimer) {
      throw new Error('No Log Timer to stop');
    }

    const durationMS = logTimer.stop();
    const eventType = logTimer.eventType;
    const data = {
      startingData: logTimer.startingData,
      endingData,
    };

    await this.log(eventType, data, durationMS, logTimer.started);
  }

  async download() {
    const ROWDELIM = '\n';
    const COLDELIM = '\t';
    const KEYS = [
      'timestamp',
      'userID',
      'robot',
      'lastConnectedRobot',
      'logSessionID',
      'eventType',
      'appVersion',
      'durationMS',
      'data',
    ];
    const csvrows: string[] = [];
    csvrows.push(KEYS.join(COLDELIM));
    const logs = await this.db?.getAll(MISSION_CONTROL_EVENT_LOG_STORE_NAME);
    logs?.forEach((log) => {
      // TODO we should better define this return type
      const csvcol: any[] = [];
      KEYS.forEach((key) => {
        csvcol.push(log[key]);
      });
      csvrows.push(csvcol.join(COLDELIM));
    });
    download(csvrows.join(ROWDELIM), 'event-log.tsv', 'text/tab-separated-values');
  }

  private async _startUploadCycle(startNow?: boolean) {
    // do not start already started or in worker
    if (this.uploadIntervalStarted || this.disabled) {
      console.debug(`Logger[${this.instanceId}]: Upload cycle already started or disabled, skipping`);

      return;
    }

    this.uploadIntervalStarted = true;
    console.debug(`Logger[${this.instanceId}] _startUploadCycle`);

    if (startNow) {
      await this._upload();
    }

    // upload loop
    this.uploadInterval = setInterval(() => {
      this._upload();
    }, MISSION_CONTROL_EVENT_LOG_UPLOAD_INTERVAL);
  }

  async _setAsUploaded(log: PartialBy<LogEntry, 'status'>) {
    if (this.disabled) {
      return;
    }

    delete log.status;
    await this.db?.put(MISSION_CONTROL_EVENT_LOG_STORE_NAME, log);
  }

  // upload small chunks of logs to dynamodb
  async _upload() {
    if (this.disabled) {
      return;
    }

    console.debug(`Logger[${this.instanceId}] _upload`);

    const local: LogEntry[] | undefined = await this.db?.getAllFromIndex(
      MISSION_CONTROL_EVENT_LOG_STORE_NAME,
      'status',
      'local',
    );

    // look for log entries with duplicate timestamps
    const duplicateTimestamps = _(local)
      .groupBy('timestamp')
      .pickBy((logs) => logs.length > 1)
      .keys()
      .value();

    // dedupe duplicate timestamps
    duplicateTimestamps.forEach((timestamp) => {
      const logs = _.filter(local, { timestamp });
      // add 1 ms to each duplicate timestamp
      logs.forEach((log, i) => {
        const newTimestamp = new Date(new Date(timestamp).getTime() + i + 1).toISOString();
        log.timestamp = newTimestamp;
      });
    });

    const logsToUpload = _.slice(local, 0, MISSION_CONTROL_EVENT_LOG_UPLOAD_MAX_LOGS);

    if (logsToUpload.length === 0) {
      console.debug(`Logger[${this.instanceId}]: No logs to upload`);

      return;
    }

    const timerLabel = `Logger[${this.instanceId}] _upload ${logsToUpload.length} log(s) attempted`;
    let countSuccess = 0;
    let overallTimeoutTriggered = false;
    console.time(timerLabel);

    const logChunks = _(logsToUpload).chunk(MISSION_CONTROL_EVENT_LOG_UPLOAD_MAX_CONCURRENT).value().entries();

    const uploadStart = Date.now();

    // @ts-ignore
    await processChunks(logChunks, async (logs) => {
      const now = Date.now();

      if (now - uploadStart >= MISSION_CONTROL_EVENT_LOG_OVERALL_UPLOAD_TIMEOUT) {
        overallTimeoutTriggered = true;

        return Promise.reject('Logger max overall timeout');
      }

      // Convert logs to API format
      const apiLogs: McApiLogEntry[] = logs.map((log) => ({
        userId: log.userID,
        timestamp: log.timestamp,
        durationMs: log.durationMS,
        appVersion: log.appVersion,
        robot: log.robot,
        data: log.data,
        status: log.status,
        logSessionId: log.logSessionID,
        eventType: log.eventType,
        lastConnectedRobot: log.lastConnectedRobot,
        jobId: log.jobId,
      }));

      // Upload batch to API
      const result = await mcApi.sendLogsToDynamo(apiLogs);

      if (result.success) {
        countSuccess += result.uploaded;
        // Mark all logs in this batch as uploaded
        await Promise.all(logs.map((log) => this._setAsUploaded(log)));
      } else {
        console.warn(`Logger[${this.instanceId}] Failed to upload batch:`, result.errors);
      }
    });

    console.timeEnd(timerLabel);
    console.debug(
      `Logger[${this.instanceId}] ${countSuccess} log(s) successfully uploaded${overallTimeoutTriggered ? ` before ${MISSION_CONTROL_EVENT_LOG_OVERALL_UPLOAD_TIMEOUT / 1000} second timeout triggered` : ''}`,
    );
  }

  _cancelUploadCycle() {
    console.debug(`Logger[${this.instanceId}] _cancelUploadCycle`);
    this.uploadIntervalStarted = false;
    clearInterval(this.uploadInterval);
  }

  async _expireRecords() {
    // expire records more than 2 days old
    const expiration = new Date(
      Date.now() - MISSION_CONTROL_EVENT_LOG_EXPIRATION_PERIOD_DAYS * 24 * 60 * 60 * 1000,
    ).toISOString();

    const range = IDBKeyRange.upperBound(expiration);
    let cursor = await this.db
      ?.transaction(MISSION_CONTROL_EVENT_LOG_STORE_NAME, 'readwrite')
      .store.index('timestamp')
      .openCursor(range);

    while (cursor) {
      cursor.delete();
      cursor = await cursor.continue();
    }
  }

  async initialize() {
    // If already initialized, return immediately
    if (this.initialized) {
      return;
    }

    // If initialization is in progress, return the existing promise
    if (MissionControlEventLogger.initializationPromise) {
      console.debug(`Logger[${this.instanceId}]: Initialization in progress, waiting`);

      return MissionControlEventLogger.initializationPromise;
    }

    console.debug(`Logger[${this.instanceId}]: Starting new initialization`);
    // Assign promise immediately to block concurrent calls
    MissionControlEventLogger.initializationPromise = new Promise<void>((resolve, reject) => {
      (async () => {
        try {
          if (typeof window === 'undefined' || !('indexedDB' in window)) {
            console.warn(`Logger[${this.instanceId}]: IndexedDB not supported`);
            this.initialized = false;
            resolve();

            return;
          }

          console.log(`Logger[${this.instanceId}] _initialize - start`);

          this.db = await openDB(MISSION_CONTROL_EVENT_LOG_DB_NAME, 3, {
            upgrade(upgradeDB, oldVersion, newVersion, transaction) {
              let store: IDBPObjectStore<unknown, ArrayLike<string>, 'EventLog', 'versionchange'> | undefined =
                undefined;

              if (!upgradeDB.objectStoreNames.contains(MISSION_CONTROL_EVENT_LOG_STORE_NAME)) {
                store = upgradeDB.createObjectStore(MISSION_CONTROL_EVENT_LOG_STORE_NAME, {
                  keyPath: 'id',
                  autoIncrement: true,
                });
              } else {
                store = transaction.objectStore(MISSION_CONTROL_EVENT_LOG_STORE_NAME);
              }

              if (!store.indexNames.contains('status')) {
                store.createIndex('status', 'status', { unique: false });
              }

              if (!store.indexNames.contains('timestamp')) {
                store.createIndex('timestamp', 'timestamp');
              }
            },
          });

          // check for records that need to be expired
          await this._expireRecords();

          // start upload loop
          await this._startUploadCycle();
          this.initialized = true;

          if (this.db && !this.disabled) {
            await this.log('LOGGER_INTIALIZED');
          }

          resolve();
        } catch (error) {
          console.error(`Logger[${this.instanceId}]: Initialization failed`, error);
          Sentry.captureException(error, { tags: { component: 'MissionControlEventLogger', action: 'initialize' } });
          this.initialized = false;
          reject(error);
        } finally {
          MissionControlEventLogger.initializationPromise = null;
          console.log(`Logger[${this.instanceId}] _initialize - end - initialized: ${this.initialized}`);
        }
      })();
    });

    return MissionControlEventLogger.initializationPromise;
  }
}

class LogTimer {
  eventType: string;
  startingData: string;
  started: number;
  ended: number;
  constructor(eventType: string, startingData: string) {
    this.eventType = eventType;
    this.startingData = startingData;
    this.started = Date.now();
  }

  stop() {
    this.ended = Date.now();

    return this.ended - this.started;
  }
}
